import { LogOut } from 'lucide-react';
import React from 'react';

import { useFoodSearchIndex } from '@/client-search/useFoodSearchIndex';
import { getLocalStorageStats } from '@/components/LocalStoragePopup';
import { useFeatures } from '@/context/FeatureContext';
import { formatNumber, formatPct } from '@/food-editor/utils/utils';
import { useBrowserNotifications } from '@/services/BrowserNotificationService';
import { alertDialog, confirmDialog } from '@/services/DialogService';
import { useSocketTelemetryData } from '@/socketio/SocketIOService';
import { humanFileSize } from '@/utils/numerical';

interface Props {
  handleLogout: () => void;
}

const ProfileAction = ({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) => (
  <button
    type="button"
    className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left text-sm text-ink transition-colors hover:bg-canvas focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
    onClick={onClick}
  >
    {children}
  </button>
);

const ProfileTab = ({ handleLogout }: Props) => {
  const notifications = useBrowserNotifications();
  const flags = useFeatures();
  const searchIndexRes = useFoodSearchIndex();
  const networkStatus = useSocketTelemetryData();
  const localStorageStats = React.useMemo(() => getLocalStorageStats(), []);

  const getNetworkSecondaryText = () => {
    if (!networkStatus.rttTotal) {
      return null;
    }

    return [
      ['avg', networkStatus.rttMean],
      ['p90', networkStatus.rttp90],
      ['err', networkStatus.rttErrorRate],
      ['count', networkStatus.rttTotal],
    ]
      .map(
        ([label, value]) =>
          `${label}: ${label !== 'err' ? formatNumber(value as number, 0) : formatPct(value as number, 0)}`,
      )
      .join(' / ');
  };

  const networkTitle = networkStatus.rttLatest
    ? `Latency: ${networkStatus.rttLatest} ms`
    : !networkStatus.rttTotal
      ? 'Connecting…'
      : networkStatus.rttLatest === null
        ? 'Connected'
        : 'Disconnected';

  return (
    <nav className="divide-y divide-line">
      <ProfileAction>
        <span>
          <span className="block font-medium">{networkTitle}</span>
          {getNetworkSecondaryText() && (
            <span className="mt-1 block text-xs text-ink-muted">{getNetworkSecondaryText()}</span>
          )}
        </span>
      </ProfileAction>

      <ProfileAction
        onClick={() => {
          if (notifications.permission === 'granted') {
            notifications.notify('Test notification');
          } else {
            notifications.requestPermission();
          }
        }}
      >
        <span>
          <span className="block font-medium">
            Notifications: {notifications.permission} {!flags.notification_sound && '(sound disabled)'}
          </span>
          <span className="mt-1 block text-xs text-ink-muted">
            {notifications.sound.isError
              ? `Sound: ${notifications.sound.error}`
              : !notifications.sound.isDone
                ? `Sound: ${notifications.sound.state}`
                : notifications.lastTitle
                  ? `Last: ${notifications.lastTitle}`
                  : null}
          </span>
        </span>
      </ProfileAction>

      <ProfileAction
        onClick={() => {
          void alertDialog(
            [
              'Top keys:',
              ...localStorageStats.keysAndSizes.slice(0, 10).map(({ key, size }) => `${key} (${humanFileSize(size)})`),
            ].join('\n'),
            { title: 'Local storage' },
          );
        }}
      >
        <span className="font-medium">Local storage: {humanFileSize(localStorageStats.total)}</span>
      </ProfileAction>

      <ProfileAction
        onClick={async () => {
          if (await confirmDialog('Reload search index?')) {
            searchIndexRes.reload();
          }
        }}
      >
        <span>
          <span className="block font-medium">
            Search index: {searchIndexRes.loadState.state} ({formatNumber(searchIndexRes.indexSize)} items)
          </span>
          {searchIndexRes.loadState.isError && (
            <span className="mt-1 block text-xs text-critical">{String(searchIndexRes.loadState.error)}</span>
          )}
        </span>
      </ProfileAction>

      <ProfileAction onClick={handleLogout}>
        <LogOut className="size-4" />
        <span className="font-medium">Logout</span>
      </ProfileAction>
    </nav>
  );
};

export default ProfileTab;
